import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Expense, CreateExpenseInput } from '@/types/database/expenses';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export const useExpenses = (filters?: { 
  department_id?: string; 
  category_id?: string;
  payment_status?: string;
  date_from?: string;
  date_to?: string;
  bank_account_id?: string;
  business_unit?: string;
}) => {
  return useQuery({
    queryKey: ['expenses', filters],
    queryFn: async () => {
      let query = (supabase as any)
        .from('expenses')
        .select(`
          *,
          category:expense_categories(*),
          department:departments(*),
          bank_account:bank_accounts(id, name, bank_name),
          bu_allocations:expense_bu_allocations(business_unit, percentage)
        `)
        .order('expense_date', { ascending: false });
      
      if (filters?.department_id) {
        query = query.eq('department_id', filters.department_id);
      }
      if (filters?.category_id) {
        query = query.eq('category_id', filters.category_id);
      }
      if (filters?.payment_status) {
        query = query.eq('payment_status', filters.payment_status);
      }
      if (filters?.date_from) {
        query = query.gte('expense_date', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('expense_date', filters.date_to);
      }
      if (filters?.bank_account_id) {
        query = query.eq('bank_account_id', filters.bank_account_id);
      }
      if (filters?.business_unit) {
        query = query.eq('business_unit', filters.business_unit);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as unknown as Expense[];
    },
  });
};

export const useExpense = (id: string) => {
  return useQuery({
    queryKey: ['expense', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('expenses')
        .select(`
          *,
          category:expense_categories(*),
          department:departments(*),
          documents:expense_documents(*),
          bank_account:bank_accounts(id, name, bank_name)
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as unknown as Expense;
    },
    enabled: !!id,
  });
};

export const useCreateExpense = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (input: CreateExpenseInput) => {
      // Generate expense number
      const { data: numberData, error: numberError } = await supabase
        .rpc('generate_expense_number');
      
      if (numberError) throw numberError;
      
      const { data, error } = await (supabase as any)
        .from('expenses')
        .insert({
          ...input,
          expense_number: numberData,
          created_by: user?.id,
          payment_status: 'paid',
          payment_date: input.payment_date || input.expense_date,
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Expense recorded successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to record expense: ${error.message}`);
    },
  });
};

export const useUpdateExpense = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<Expense> & { id: string }) => {
      const { data, error } = await (supabase as any)
        .from('expenses')
        .update(input)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Expense updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update expense: ${error.message}`);
    },
  });
};

export const useMarkExpensePaid = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ id, payment_method, payment_reference, bank_account_id }: { 
      id: string; 
      payment_method?: string; 
      payment_reference?: string;
      bank_account_id?: string;
    }) => {
      const { data, error } = await supabase
        .from('expenses')
        .update({
          payment_status: 'paid',
          payment_date: new Date().toISOString().split('T')[0],
          payment_method,
          payment_reference,
          bank_account_id,
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Expense marked as paid');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update expense: ${error.message}`);
    },
  });
};

export const useExpenseStats = (filters?: { date_from?: string; date_to?: string }) => {
  return useQuery({
    queryKey: ['expense-stats', filters],
    queryFn: async () => {
      let query = supabase
        .from('expenses')
        .select('total_amount, payment_status, category_id, department_id');
      
      if (filters?.date_from) {
        query = query.gte('expense_date', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('expense_date', filters.date_to);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const total = data.reduce((sum, e) => sum + (e.total_amount || 0), 0);
      const paid = data.filter(e => e.payment_status === 'paid').reduce((sum, e) => sum + (e.total_amount || 0), 0);
      const pending = data.filter(e => e.payment_status === 'pending').reduce((sum, e) => sum + (e.total_amount || 0), 0);
      
      return {
        total,
        paid,
        pending,
        count: data.length,
      };
    },
  });
};
